/**
 * BroadCast関連のユーティリティ関数
 * 
 * このファイルには、BroadCast配信フォームで使用される
 * バリデーションとデータ変換のヘルパー関数が含まれています。
 */
import { BroadCastFormType } from "src/models/broadCast";

/**
 * 送信内容タイプ別バリデーション関数
 */
export const validateContentByType = (
    value: BroadCastFormType,
    status: 'draft' | 'reserved',
    image?: File,
    templateImageUrl?: string | null
) => {
    // 配信確定時のみタイトル必須
    if (!value.title || value.title.trim() === '') {
        throw new Error('タイトルを入力してください');
    }

    // 配信確定時のみ送信日時必須
    if (status === 'reserved' && !value.sendAt) {
        throw new Error('送信日時を設定してください');
    }
    
    // 送信するアカウントは常に必須（通常配信・セグメント配信共通）
    if (!value.storeIds || value.storeIds.length === 0) {
        throw new Error('送信するアカウントを選択してください');
    }

    // 送信内容タイプが未選択の場合
    if (value.type === undefined) {
        throw new Error('送信内容タイプを選択してください');
    }

    const _needText = value.type === 'text' || value.type === 'textAndImage' || value.type === 'textAndTemplate';
    const _needImage = value.type === 'image' || value.type === 'textAndImage';
    const _needTemplate = value.type === 'template' || value.type === 'textAndTemplate';

    // テキスト必須チェック
    if (_needText && (!value.text || value.text.trim() === '')) {
        throw new Error('テキストを入力してください');
    }

    // 画像必須チェック
    if (_needImage && !image && !templateImageUrl && !value.broadCast?.message?.imageUrl) {
        throw new Error('画像をアップロードしてください');
    }

    // テンプレート必須チェック
    if (_needTemplate) {
        if (!image && !templateImageUrl && !value.broadCast?.message?.tempImageUrl) {
            throw new Error('画像をアップロードしてください');
        }
        if (!value.tempLabel || value.tempLabel.trim() === '') {
            throw new Error('画像上のテキストを入力してください');
        }
        if (!value.altText || value.altText.trim() === '') {
            throw new Error('通知に表示されるテキストを入力してください');
        }
        if (!value.tempUri || value.tempUri.trim() === '') {
            throw new Error('リンク先を入力してください');
        }
    }
};

/**
 * セグメント条件内の日付フィールドをmomentオブジェクトからDateオブジェクトに変換する関数
 * @throws {Error} セグメント条件の形式が不正な場合
 */
export const convertSegmentConditions = (segmentConditions: any) => {
    if (!segmentConditions) return null;

    try {
        const converted = { ...segmentConditions };

        // specificDateVisitのtargetDatesを変換
        if (converted.specificDateVisit?.targetDates) {
            if (!Array.isArray(converted.specificDateVisit.targetDates)) {
                throw new Error('特定日来店の日付形式が不正です');
            }
            converted.specificDateVisit.targetDates = converted.specificDateVisit.targetDates.map((date: any) => {
                return date && typeof date.toDate === 'function' ? date.toDate() : date;
            });
        }

        // visitPatternのdateRangeとspecificDatesも変換
        if (converted.visitPattern?.dateRange) {
            if (!Array.isArray(converted.visitPattern.dateRange)) {
                throw new Error('来店パターンの期間形式が不正です');
            }
            converted.visitPattern.dateRange = converted.visitPattern.dateRange.map((date: any) => {
                return date && typeof date.toDate === 'function' ? date.toDate() : date;
            });
        }

        if (converted.visitPattern?.specificDates) {
            if (!Array.isArray(converted.visitPattern.specificDates)) {
                throw new Error('来店パターンの日付形式が不正です');
            }
            converted.visitPattern.specificDates = converted.visitPattern.specificDates.map((date: any) => {
                return date && typeof date.toDate === 'function' ? date.toDate() : date;
            });
        }

        return converted;
    } catch (error) {
        console.error('セグメント条件の変換に失敗:', error);
        throw new Error('セグメント条件の形式が不正です');
    }
}